import {
  Form,
  type OperationType,
  SelectField,
  SubmitButton,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { useProject } from '@/features/project/_hooks/project';
import {
  IndicatorFormSchema,
  IndicatorTypeLabelModifier,
  indicatorType as indicatorTypeModel,
} from '@/features/project/projects/_models/activity';
import { useForm, useId } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof IndicatorFormSchema>;

type Props = {
  type: OperationType;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const IndicatorForm: FC<Props> = ({ type, initialValues, onSubmit }) => {
  const { t } = useTranslation();
  const { id } = useId();
  const { data: project } = useProject(id);
  const fiscalYear = project?.fiscalYear ?? 2025;
  const {
    control,
    formState: { isDirty, errors, isSubmitting, isSubmitSuccessful },
    handleSubmit,
    // watch,
    // resetField,
    // clearErrors,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(IndicatorFormSchema),
  });

  // const [indicatorType, dataNatureType] = watch(['type', 'dataNatureType']);

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="type"
        label={t('indicator-type')}
        control={control}
        options={indicatorTypeModel.options.map((value) => ({
          value,
          label: IndicatorTypeLabelModifier[value],
        }))}
        errors={errors}
        required
        disabled={type === 'edit'}
      />

      <TextAreaInputField
        name="description"
        label={t('indicator-description')}
        control={control}
        errors={errors}
        rows={3}
        required
      />
      <TextInputField
        name="unit"
        label={t('unit')}
        control={control}
        errors={errors}
        maxLength={20}
        fallbackValue={null}
      />

      <TextAreaInputField
        name="reason"
        label={fiscalYear.toString()}
        control={control}
        errors={errors}
      />

      <TextAreaInputField
        name="noTypeReason"
        label={(fiscalYear - 1).toString()}
        control={control}
        errors={errors}
      />

      {/* <SelectField
        name="term"
        label={t('term')}
        hint={t('hint-indicator-term')}
        control={control}
        options={termType.options.map((value) => ({
          value,
          label: TermTypeLabelModifier[value],
        }))}
        errors={errors}
        placeholder={indicatorType === 'output' ? '-' : undefined}
        required={indicatorType === 'outcome'}
        disabled={indicatorType === 'output'}
      />

      <SelectField
        name="dataNatureType"
        label={t('data-nature-type')}
        hint={t('hint-indicator-data-nature-type')}
        control={control}
        errors={errors}
        options={_dataNatureType.options.map((value) => ({
          value,
          label: DataNatureTypeLabelModifier[value],
        }))}
        placeholder={indicatorType === 'output' ? '-' : undefined}
        disabled={indicatorType === 'output'}
        required={indicatorType === 'outcome'}
        shouldCoexistOnChange
        onChange={(dataNatureType) => {
          if (dataNatureType === 'qualitative') {
            clearErrors(['targetValue', 'unit']);
            if (indicatorType === 'outcome') {
              resetField('unit', {
                keepDirty: false,
                keepTouched: false,
                keepError: false,
                defaultValue: null,
              });
              resetField('evaluationDirection', {
                keepDirty: false,
                keepTouched: false,
                keepError: false,
                defaultValue: null,
              });
            }
          }
          if (dataNatureType === 'quantitative') {
            clearErrors('noTypeReason');
            if (indicatorType === 'outcome') {
              resetField('noTypeReason', {
                keepDirty: false,
                keepTouched: false,
                keepError: false,
                defaultValue: null,
              });
            }
          }
        }}
      /> */}

      {/* <TextAreaInputField
        name="title"
        label={t('indicator-title')}
        hint={t('hint-indicator-title')}
        control={control}
        errors={errors}
        rows={3}
        required
      /> */}

      {/* <NumericFormatField
        name="sortOrder"
        label={t('sort-order')}
        hint={t('hint-indicator-sort-order')}
        control={control}
        errors={errors}
        required
        allowNegative={false}
      /> */}

      {/* <NumericFormatField
        name="targetValue"
        label={t('indicator-target-value')}
        control={control}
        errors={errors}
        decimalScale={3}
        disabled
      /> */}

      {/* <SelectField
        name="evaluationDirection"
        label={t('evaluation-direction')}
        hint={t('hint-indicator-evaluation-direction')}
        control={control}
        errors={errors}
        options={evaluationDirectionType.options.map((value) => ({
          value,
          label: EvaluationDirectionTypeLabelModifier[value],
        }))}
        disabled={
          indicatorType === 'output' ||
          (indicatorType === 'outcome' && dataNatureType === 'qualitative')
        }
        required={
          indicatorType === 'outcome' && dataNatureType === 'quantitative'
        }
      />

      <DatePickerField
        name="fiscalYear"
        label={t('fiscal-year')}
        picker="year"
        control={control}
        errors={errors}
        disabled
      /> */}

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
